import { useEffect, useRef, useState } from 'react';

/**
 * Day config — warm, bright sky with soft clouds.
 * Based on user's reference link with adjustments for light readability.
 */
const DAY_CONFIG = {
  mouseControls: true,
  touchControls: true,
  gyroControls: false,
  mouseEase: true,
  scale: 3,
  scaleMobile: 12,
  backgroundColor: 0xf0f4ff,
  skyColor: 0x68afd7,
  cloudColor: 0xadc1de,
  cloudShadowColor: 0x183550,
  sunColor: 0xffa347,
  sunGlareColor: 0xff9f33,
  sunlightColor: 0xffa373,
  speed: 0.5,
};

/**
 * Night config — midnight navy, no green, no violet. Pure deep blue.
 */
const NIGHT_CONFIG = {
  mouseControls: true,
  touchControls: true,
  gyroControls: false,
  mouseEase: true,
  scale: 3,
  scaleMobile: 12,
  backgroundColor: 0x030712,
  skyColor: 0x0c1a30,
  cloudColor: 0x5580aa,
  cloudShadowColor: 0x0a1525,
  sunColor: 0x8ab4d8,
  sunGlareColor: 0x6090c0,
  sunlightColor: 0xa0c8e8,
  speed: 0.4,
};

/**
 * Dynamically loads Vanta.js clouds effect with day/night switching
 * based on system `prefers-color-scheme`. Respects `prefers-reduced-motion`.
 *
 * Returns a ref to attach to the background container div.
 * The hook handles init, destroy, theme changes, and WebGL failure gracefully.
 */
export function useVantaClouds() {
  const containerRef = useRef<HTMLDivElement>(null);
  const vantaRef = useRef<any>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // ponytail: bail entirely if user prefers reduced motion — no GPU waste
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motionQuery.matches) return;

    let cancelled = false;

    const themeQuery = window.matchMedia('(prefers-color-scheme: dark)');

    function getConfig() {
      return themeQuery.matches ? NIGHT_CONFIG : DAY_CONFIG;
    }

    async function initVanta() {
      try {
        // Dynamic import — keeps Three.js out of the initial bundle
        const [THREE, { default: CLOUDS }] = await Promise.all([
          import('three'),
          import('vanta/dist/vanta.clouds.min'),
        ]);

        if (cancelled || !containerRef.current) return;

        // Destroy any existing instance before creating a new one
        if (vantaRef.current) {
          vantaRef.current.destroy();
          vantaRef.current = null;
        }

        vantaRef.current = CLOUDS({
          el: containerRef.current,
          THREE,
          ...getConfig(),
        });

        setLoaded(true);
      } catch {
        // ponytail: WebGL unavailable or import failed — silent fallback to CSS gradient
        // The CSS already has a static gradient fallback on .login-page
        setLoaded(false);
      }
    }

    function handleThemeChange() {
      // Reinitialize with the new config — Vanta doesn't support live param updates for all fields
      if (!cancelled) initVanta();
    }

    initVanta();
    themeQuery.addEventListener('change', handleThemeChange);

    // Also handle reduced-motion changes at runtime
    function handleMotionChange(e: MediaQueryListEvent) {
      if (e.matches && vantaRef.current) {
        vantaRef.current.destroy();
        vantaRef.current = null;
        setLoaded(false);
      } else if (!e.matches) {
        initVanta();
      }
    }
    motionQuery.addEventListener('change', handleMotionChange);

    return () => {
      cancelled = true;
      themeQuery.removeEventListener('change', handleThemeChange);
      motionQuery.removeEventListener('change', handleMotionChange);
      if (vantaRef.current) {
        vantaRef.current.destroy();
        vantaRef.current = null;
      }
    };
  }, []);

  return { containerRef, loaded };
}
